/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Box, FormControl, IconButton, InputAdornment, RadioGroup } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddIcon from "@mui/icons-material/Add";
import "react-phone-input-2/lib/material.css";
import api from "../../../api";
import { Query_Keys } from "../../../lib/queryKeys";
import { PerPage } from "../../../constants/perPage";
import { useDebounce } from "../../../utils/useDebounce";
import { formatDate } from "../../../utils/formatDate";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { Loader } from "../../../components/loader";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { LoadingSpinner } from "../../../components/loading_spinner";
import { ModalCancelButton } from "../../../components/modal_cancel_button";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { restaurantGroupMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/restaurantGroupMutationKeys";
import {
  CLOSE_ICON_COLOR,
  CustomStyledFormFieldText,
  FieldStack,
  FormContent,
  HelperError,
  InlineClearIconButton,
  PartnerCard,
  PartnerCardDescription,
  PartnerCardGroup,
  PartnerCardHeader,
  PartnerCardIcon,
  PartnerCardRadio,
  PartnerCardTitle,
  PartnerCategoryGroup,
  PartnerCategoryOption,
  PartnerSectionDivider,
  PartnershipDialog,
  PartnershipDialogContent,
  PartnershipDialogHeader,
  PartnershipDialogTitle,
  PartnershipSubmitButton,
  PairLabelText,
  REQUIRED_FIELD_ASTERISK_COLOR,
  SelectClearButton,
  StyledDialogActions,
  StyledPhoneInput,
  StyledSelect,
  StyledTextField,
  SubtitleText,
  TwoColumnGrid,
} from "./styles";
import { PartnershipSuccessModal } from "../partnership_success_modal";

const MODAL_SUBTITLE =
  "A partner is either another restaurant group or a business partner. Benefit programs and their provider/recipient direction are configured later.";
const DRAFT_STATUS = "draft";
// Mirrors CentralTimeZoneSupport::CENTRAL_TIME_ZONE on the backend, which is the zone the
// stamp comes back in.
const CENTRAL_TIME_ZONE = "America/Chicago";
const EMPTY_RESTAURANT_GROUPS = [];
const RESTAURANT_GROUP_SEARCH_RESET_DELAY_MS = 1000;
const CREATE_MODE = "create";
const EDIT_MODE = "edit";
// A partnership side is typed on the backend, and `mode` is required — it picks the write
// path: group-to-group, or group-to-restaurant (a "business partner").
const RESTAURANT_GROUP_TYPE = "RestaurantGroup";
const RESTAURANT_TYPE = "Restaurant";
const GROUP_PARTNERSHIP_MODE = "group";
const BUSINESS_PARTNER_MODE = "business_partner";
const PARTNER_FIELD = "relationship_entity_two_id";

// Level-1 tab (which kind of partner) and level-2 card (how to source the business partner)
// — named distinctly from the create/edit `mode` prop above to avoid confusion with it.
const GROUP_TAB = "group";
const BUSINESS_PARTNER_TAB = "business_partner";
const EXISTING_PARTNER_CARD = "existing";
const NEW_PARTNER_CARD = "new";

const PARTNER_TYPE_OPTIONS = [
  "Fitness/Wellness",
  "Hotel/Hospitality",
  "Retail",
  "Entertainment",
  "Corporate",
  "Custom",
];
const CUSTOM_PARTNER_TYPE = "Custom";
const DEFAULT_PHONE_COUNTRY_CODE = "+1";
const EMPTY_RESTAURANTS = [];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "inactive", label: "Inactive" },
  { value: "active", label: "Active" },
];

const SUBMIT_LABELS = {
  [CREATE_MODE]: {
    draft: "Create Draft",
    inactive: "Add Inactive Partner",
    active: "Add Partner",
  },
  [EDIT_MODE]: {
    draft: "Update Draft",
    inactive: "Update Inactive Partner",
    active: "Update Partner",
  },
};

const getFirstError = (value) => {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value || "";
};

// Today in central time rather than the browser's, so the preview matches the date the
// backend will stamp no matter where the admin is sitting.
const centralTodayLabel = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CENTRAL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZoneName: "short",
  }).formatToParts(new Date());

  const partValue = (type) => parts.find((part) => part.type === type)?.value || "";

  return `${partValue("month")}/${partValue("day")}/${partValue("year")} (${partValue("timeZoneName")})`;
};

const PartnershipModal = ({
  open,
  onClose,
  mode = CREATE_MODE,
  restaurantGroup,
  partnershipId,
}) => {
  const isEditMode = mode === EDIT_MODE;
  const showNotification = useNotificationContext();
  const [partnerRestaurantGroupId, setPartnerRestaurantGroupId] = React.useState("");
  const [relationshipName, setRelationshipName] = React.useState("");
  const [status, setStatus] = React.useState(DRAFT_STATUS);
  const [description, setDescription] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = React.useState("");
  const [partnerOptionsCache, setPartnerOptionsCache] = React.useState([]);
  const searchResetTimeoutRef = React.useRef(null);
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);

  const [partnerCategory, setPartnerCategory] = React.useState(GROUP_TAB);
  const [businessPartnerCard, setBusinessPartnerCard] = React.useState(EXISTING_PARTNER_CARD);
  const [partnerRestaurantId, setPartnerRestaurantId] = React.useState("");
  const [restaurantSearch, setRestaurantSearch] = React.useState("");
  const [restaurantOptionsCache, setRestaurantOptionsCache] = React.useState([]);
  const restaurantSearchResetTimeoutRef = React.useRef(null);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);

  const [newPartnerName, setNewPartnerName] = React.useState("");
  const [newPartnerPhone, setNewPartnerPhone] = React.useState("");
  const [newPartnerPhoneCountryCode, setNewPartnerPhoneCountryCode] = React.useState(
    DEFAULT_PHONE_COUNTRY_CODE,
  );
  const [newPartnerEmail, setNewPartnerEmail] = React.useState("");
  const [newPartnerWebsite, setNewPartnerWebsite] = React.useState("");
  const [newPartnerAddress, setNewPartnerAddress] = React.useState("");
  const [newPartnerType, setNewPartnerType] = React.useState("");
  const [newPartnerTypeIsCustom, setNewPartnerTypeIsCustom] = React.useState(false);
  // Holds the restaurant the backend created, which is what the success dialog offers to
  // finish setting up.
  const [createdRestaurant, setCreatedRestaurant] = React.useState(null);

  const resetForm = React.useCallback(() => {
    setPartnerRestaurantGroupId("");
    setRelationshipName("");
    setStatus(DRAFT_STATUS);
    setDescription("");
    setErrors({});
    setIsSubmitting(false);
    setRestaurantGroupSearch("");
    setPartnerCategory(GROUP_TAB);
    setBusinessPartnerCard(EXISTING_PARTNER_CARD);
    setPartnerRestaurantId("");
    setRestaurantSearch("");
    setNewPartnerName("");
    setNewPartnerPhone("");
    setNewPartnerPhoneCountryCode(DEFAULT_PHONE_COUNTRY_CODE);
    setNewPartnerEmail("");
    setNewPartnerWebsite("");
    setNewPartnerAddress("");
    setNewPartnerType("");
    setNewPartnerTypeIsCustom(false);
  }, []);

  React.useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const {
    data: partnership,
    isLoading: isLoadingPartnership,
    isError: hasPartnershipLoadError,
  } = useQuery({
    queryKey: Query_Keys.restaurant_group_partnership({ id: partnershipId }),
    queryFn: async () => {
      const response = await api.get(
        `/v1/admin/restaurant_group_partnerships/${partnershipId}`,
      );

      return response?.data;
    },
    enabled: Boolean(open && isEditMode && partnershipId),
  });

  // Declared after the reset effect so a cached partnership still wins on reopen.
  React.useEffect(() => {
    if (!open || !isEditMode || !partnership) {
      return;
    }

    setRelationshipName(partnership.relationship_name || "");
    setStatus(partnership.status || DRAFT_STATUS);
    setDescription(partnership.description || "");
  }, [open, isEditMode, partnership]);

  React.useEffect(() => {
    if (!open || !hasPartnershipLoadError) {
      return;
    }

    showNotification("Failed to load partnership details", "error");
    onClose();
  }, [open, hasPartnershipLoadError, showNotification, onClose]);

  React.useEffect(
    () => () => {
      if (searchResetTimeoutRef.current) {
        window.clearTimeout(searchResetTimeoutRef.current);
      }
    },
    [],
  );

  const fetchRestaurantGroupList = React.useCallback(
    async ({ pageParam = 1 }) => {
      const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
        params: {
          page: pageParam,
          per_page: PerPage,
          search: debouncedRestaurantGroupSearch || undefined,
        },
      });

      return response?.data?.map(normalizeRestaurantGroup) || [];
    },
    [debouncedRestaurantGroupSearch],
  );

  const {
    data: restaurantGroups = EMPTY_RESTAURANT_GROUPS,
    isFetching: restaurantGroupsFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
    }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: Boolean(open && !isEditMode),
  });

  const { rootRef: partnerMenuRef, targetRef: partnerLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  // The group the modal was opened from cannot partner with itself, so it never appears
  // as a selectable option.
  const partnerOptions = React.useMemo(
    () =>
      restaurantGroups.filter(
        (option) => String(option?.label) !== String(restaurantGroup?.id),
      ),
    [restaurantGroups, restaurantGroup?.id],
  );

  React.useEffect(() => {
    setPartnerOptionsCache((currentOptions) =>
      mergeOptionsIfChanged(currentOptions, partnerOptions, "label", ["value"]),
    );
  }, [partnerOptions]);

  const partnerDisplayName = React.useMemo(() => {
    const selectedOption = partnerOptionsCache.find(
      (option) => String(option?.label) === String(partnerRestaurantGroupId),
    );

    return selectedOption?.value || "";
  }, [partnerOptionsCache, partnerRestaurantGroupId]);

  const clearFieldError = React.useCallback((field) => {
    setErrors((previousErrors) => {
      if (!previousErrors?.[field]) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  }, []);

  const handleMenuClose = React.useCallback(() => {
    if (searchResetTimeoutRef.current) {
      window.clearTimeout(searchResetTimeoutRef.current);
    }

    searchResetTimeoutRef.current = window.setTimeout(() => {
      setRestaurantGroupSearch("");
      searchResetTimeoutRef.current = null;
    }, RESTAURANT_GROUP_SEARCH_RESET_DELAY_MS);
  }, []);

  React.useEffect(
    () => () => {
      if (restaurantSearchResetTimeoutRef.current) {
        window.clearTimeout(restaurantSearchResetTimeoutRef.current);
      }
    },
    [],
  );

  const isExistingPartnerCardActive =
    partnerCategory === BUSINESS_PARTNER_TAB && businessPartnerCard === EXISTING_PARTNER_CARD;

  const fetchRestaurantList = React.useCallback(
    async ({ pageParam = 1 }) => {
      const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
        params: {
          page: pageParam,
          per_page: PerPage,
          search: debouncedRestaurantSearch || undefined,
        },
      });

      return response?.data?.map(normalizeRestaurant) || [];
    },
    [debouncedRestaurantSearch],
  );

  const {
    data: restaurants = EMPTY_RESTAURANTS,
    isFetching: restaurantsFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({ search: debouncedRestaurantSearch }),
    queryFn: fetchRestaurantList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: Boolean(open && isExistingPartnerCardActive),
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) =>
      mergeOptionsIfChanged(currentOptions, restaurants, "id", ["name"]),
    );
  }, [restaurants]);

  const restaurantDisplayName = React.useMemo(() => {
    const selectedOption = restaurantOptionsCache.find(
      (option) => String(option?.id) === String(partnerRestaurantId),
    );

    return selectedOption?.name || "";
  }, [restaurantOptionsCache, partnerRestaurantId]);

  const handleRestaurantMenuClose = React.useCallback(() => {
    if (restaurantSearchResetTimeoutRef.current) {
      window.clearTimeout(restaurantSearchResetTimeoutRef.current);
    }

    restaurantSearchResetTimeoutRef.current = window.setTimeout(() => {
      setRestaurantSearch("");
      restaurantSearchResetTimeoutRef.current = null;
    }, RESTAURANT_GROUP_SEARCH_RESET_DELAY_MS);
  }, []);

  const handlePhoneChange = React.useCallback((value, country) => {
    const countryCode = country?.dialCode || "";
    setNewPartnerPhone(value?.replace(countryCode, "") || "");
    setNewPartnerPhoneCountryCode(countryCode ? `+${countryCode}` : DEFAULT_PHONE_COUNTRY_CODE);
    setErrors((previousErrors) => {
      if (!previousErrors?.phone_number) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors.phone_number;
      return nextErrors;
    });
  }, []);

  const handlePartnerTypeChange = React.useCallback((event) => {
    const { value } = event.target;

    if (value === CUSTOM_PARTNER_TYPE) {
      setNewPartnerTypeIsCustom(true);
      setNewPartnerType("");
    } else {
      setNewPartnerType(value);
    }
  }, []);

  const isNewPartnerFlow =
    !isEditMode &&
    partnerCategory === BUSINESS_PARTNER_TAB &&
    businessPartnerCard === NEW_PARTNER_CARD;

  const { mutateAsync: savePartnership } = useOtMutation({
    mutationFn: async (payload) => {
      if (isEditMode) {
        return api.put(`/v1/admin/restaurant_group_partnerships/${partnershipId}`, payload);
      }

      return api.post("/v1/admin/restaurant_group_partnerships", payload);
    },
    invalidateKeys: (() => {
      if (isEditMode) {
        return [...restaurantGroupMutationKeyGroups.partnershipUpdate];
      }

      // The inline create authors a restaurant too, so its caches go stale as well.
      return isNewPartnerFlow
        ? [...restaurantGroupMutationKeyGroups.partnershipBusinessPartnerCreate]
        : [...restaurantGroupMutationKeyGroups.partnershipCreate];
    })(),
    successMessage: () => {
      if (isEditMode) {
        return "Partnership has been updated successfully";
      }

      // A brand new business partner is confirmed in a dialog that offers its restaurant
      // profile, so the snackbar would only repeat it.
      return isNewPartnerFlow ? null : "Partnership has been created successfully";
    },
    errorMessage: ({ error }) => {
      const apiErrors = error?.response?.data?.errors || {};
      const fieldErrors = Object.keys(apiErrors).filter((field) => field !== "base");

      // Field-level errors render on the fields themselves; only base and unexpected
      // failures belong in the snackbar.
      if (apiErrors.base) {
        return getFirstError(apiErrors.base);
      }

      if (fieldErrors.length > 0) {
        return null;
      }

      return isEditMode ? "Failed to update partnership" : "Failed to create partnership";
    },
    onSuccess: (response) => {
      const restaurantEntity = isNewPartnerFlow
        ? response?.data?.entities?.find((entity) => entity?.type === RESTAURANT_TYPE)
        : null;

      onClose();

      if (restaurantEntity) {
        setCreatedRestaurant(restaurantEntity);
      }
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors || {});
      setIsSubmitting(false);
    },
  });

  const isFormComplete = React.useMemo(() => {
    if (isEditMode) {
      return Boolean(relationshipName.trim() && status);
    }

    if (!relationshipName.trim() || !status) {
      return false;
    }

    if (partnerCategory === GROUP_TAB) {
      return Boolean(partnerRestaurantGroupId);
    }

    if (businessPartnerCard === EXISTING_PARTNER_CARD) {
      return Boolean(partnerRestaurantId);
    }

    return Boolean(newPartnerName.trim() && newPartnerPhone.trim());
  }, [
    isEditMode,
    relationshipName,
    status,
    partnerCategory,
    partnerRestaurantGroupId,
    businessPartnerCard,
    partnerRestaurantId,
    newPartnerName,
    newPartnerPhone,
  ]);

  const handleSubmit = async () => {
    setErrors({});
    setIsSubmitting(true);

    // Update always sends description so clearing the notes reaches the backend, which
    // applies whichever keys the payload contains.
    let payload;

    if (isEditMode) {
      payload = {
        relationship_name: relationshipName.trim(),
        status,
        description: description.trim(),
      };
    } else if (partnerCategory === GROUP_TAB) {
      payload = {
        mode: GROUP_PARTNERSHIP_MODE,
        relationship_entity_one_type: RESTAURANT_GROUP_TYPE,
        relationship_entity_one_id: Number(restaurantGroup?.id),
        relationship_entity_two_type: RESTAURANT_GROUP_TYPE,
        relationship_entity_two_id: Number(partnerRestaurantGroupId),
        relationship_name: relationshipName.trim(),
        status,
        ...(description.trim() ? { description: description.trim() } : {}),
      };
    } else if (businessPartnerCard === EXISTING_PARTNER_CARD) {
      payload = {
        mode: BUSINESS_PARTNER_MODE,
        relationship_entity_one_type: RESTAURANT_GROUP_TYPE,
        relationship_entity_one_id: Number(restaurantGroup?.id),
        relationship_entity_two_type: RESTAURANT_TYPE,
        relationship_entity_two_id: Number(partnerRestaurantId),
        relationship_name: relationshipName.trim(),
        status,
        ...(description.trim() ? { description: description.trim() } : {}),
      };
    } else {
      payload = {
        mode: BUSINESS_PARTNER_MODE,
        relationship_entity_one_type: RESTAURANT_GROUP_TYPE,
        relationship_entity_one_id: Number(restaurantGroup?.id),
        relationship_entity_two_type: RESTAURANT_TYPE,
        relationship_name: relationshipName.trim(),
        status,
        restaurant_name: newPartnerName.trim(),
        phone_number: newPartnerPhone.trim(),
        phone_country_code: newPartnerPhoneCountryCode,
        ...(newPartnerEmail.trim() ? { email: newPartnerEmail.trim() } : {}),
        ...(newPartnerWebsite.trim() ? { website: newPartnerWebsite.trim() } : {}),
        ...(newPartnerAddress.trim() ? { address: newPartnerAddress.trim() } : {}),
        ...(newPartnerType.trim() ? { restaurant_type: newPartnerType.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      };
    }

    try {
      await savePartnership(payload);
    } catch {
      // Handled in the mutation callbacks.
    }
  };

  // Shows what the record will hold after this save, always in central time. The backend
  // restamps only on the transition into active, so a stored date is shown unless this
  // save is the transition that replaces it.
  const relationshipStartDate = React.useMemo(() => {
    const willRestamp = status === "active" && partnership?.status !== "active";

    if (partnership?.relationship_start_date && !willRestamp) {
      return formatDate(partnership.relationship_start_date, partnership.time_zone_abbr);
    }

    return status === "active" ? centralTodayLabel() : "";
  }, [
    partnership?.relationship_start_date,
    partnership?.time_zone_abbr,
    partnership?.status,
    status,
  ]);

  const isFormDisabled = isSubmitting || (isEditMode && isLoadingPartnership);

  const dismissCreatedRestaurant = () => setCreatedRestaurant(null);

  // Reused by the group tab and both business-partner cards — the current group is always
  // the fixed, disabled "one" side of the pair, so its markup only needs one definition.
  const renderRestaurantGroupField = () => (
    <FieldStack>
      <CustomStyledFormFieldText>
        Restaurant group
        <span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
      </CustomStyledFormFieldText>
      <StyledTextField
        fullWidth
        size="small"
        value={restaurantGroup?.name || ""}
        disabled
        inputProps={{ "data-testid": "partnership-restaurant-group-display" }}
      />
    </FieldStack>
  );

  return (
    <React.Fragment>
    <PartnershipDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      data-testid={isEditMode ? "partnership-edit-modal" : "partnership-create-modal"}
      aria-labelledby={
        isEditMode ? "partnership-edit-modal-title" : "partnership-create-modal-title"
      }
    >
      <PartnershipDialogHeader>
        <PartnershipDialogTitle
          id={isEditMode ? "partnership-edit-modal-title" : "partnership-create-modal-title"}
          tabIndex={-1}
        >
          {isEditMode ? "Edit partner" : "Add partner"}
        </PartnershipDialogTitle>
        <IconButton
          onClick={onClose}
          aria-label={`Close ${isEditMode ? "edit" : "add"} partner modal`}
        >
          <CloseIcon sx={{ color: CLOSE_ICON_COLOR }} />
        </IconButton>
      </PartnershipDialogHeader>

      <PartnershipDialogContent dividers={false}>
        <SubtitleText>{MODAL_SUBTITLE}</SubtitleText>
        {isEditMode && isLoadingPartnership ? (
          <Box
            sx={{
              minHeight: "360px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            data-testid="partnership-loading"
          >
            <Loader />
          </Box>
        ) : (
        <FormContent>
          {isEditMode ? (
            <FieldStack>
              <CustomStyledFormFieldText>Partnership</CustomStyledFormFieldText>
              <PairLabelText data-testid="partnership-pair-label">
                {partnership?.label || ""}
              </PairLabelText>
            </FieldStack>
          ) : (
          <>
          <PartnerCategoryGroup role="group" aria-label="Partner type">
            <PartnerCategoryOption
              type="button"
              disableRipple
              isSelected={partnerCategory === GROUP_TAB}
              onClick={() => setPartnerCategory(GROUP_TAB)}
              data-testid="partnership-category-group"
            >
              Restaurant Group Partner
            </PartnerCategoryOption>
            <PartnerCategoryOption
              type="button"
              disableRipple
              isSelected={partnerCategory === BUSINESS_PARTNER_TAB}
              onClick={() => setPartnerCategory(BUSINESS_PARTNER_TAB)}
              data-testid="partnership-category-business-partner"
            >
              Business Partner
            </PartnerCategoryOption>
          </PartnerCategoryGroup>

          {partnerCategory === GROUP_TAB ? (
          <TwoColumnGrid>
            {renderRestaurantGroupField()}

            <FieldStack>
              <CustomStyledFormFieldText>
                Partner restaurant group
                <span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
              </CustomStyledFormFieldText>
              <FormControl fullWidth error={Boolean(errors?.[PARTNER_FIELD])}>
                <StyledSelect
                  value={partnerRestaurantGroupId}
                  displayEmpty
                  disabled={isFormDisabled}
                  onChange={(event) => {
                    setPartnerRestaurantGroupId(event.target.value);
                    clearFieldError(PARTNER_FIELD);
                  }}
                  onClose={handleMenuClose}
                  MenuProps={{
                    PaperProps: {
                      ref: partnerMenuRef,
                      sx: { maxHeight: 400 },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  renderValue={() => partnerDisplayName || "Select partner group"}
                  data-testid="partnership-partner-group-select"
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantGroupSearch}
                    placeholder="Search restaurant groups"
                    isFetching={restaurantGroupsFetching}
                  />
                  {partnerOptions?.map((option) => (
                    <StyledMenuItem key={option?.label} value={option?.label}>
                      {String(partnerRestaurantGroupId) === String(option?.label) ? (
                        <SelectedMenuItem data={option?.value} />
                      ) : (
                        option?.value
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={partnerLoaderRef}
                  />
                </StyledSelect>
                <HelperError>{getFirstError(errors?.[PARTNER_FIELD])}</HelperError>
              </FormControl>
            </FieldStack>
          </TwoColumnGrid>
          ) : (
          <>
          <RadioGroup
            value={businessPartnerCard}
            onChange={(event) => setBusinessPartnerCard(event.target.value)}
            aria-label="Business partner source"
          >
            <PartnerCardGroup>
              <PartnerCard
                isSelected={businessPartnerCard === EXISTING_PARTNER_CARD}
                onClick={() => setBusinessPartnerCard(EXISTING_PARTNER_CARD)}
                data-testid="partnership-card-existing"
              >
                <PartnerCardHeader>
                  <PartnerCardRadio
                    value={EXISTING_PARTNER_CARD}
                    inputProps={{
                      "aria-label": "Existing partner",
                      "data-testid": "partnership-card-existing-radio",
                    }}
                  />
                  <PartnerCardIcon isSelected={businessPartnerCard === EXISTING_PARTNER_CARD}>
                    <StorefrontIcon />
                  </PartnerCardIcon>
                  <PartnerCardTitle>Existing partner</PartnerCardTitle>
                </PartnerCardHeader>
                <PartnerCardDescription>
                  Already in the system — just link it to this group.
                </PartnerCardDescription>
              </PartnerCard>
              <PartnerCard
                isSelected={businessPartnerCard === NEW_PARTNER_CARD}
                onClick={() => setBusinessPartnerCard(NEW_PARTNER_CARD)}
                data-testid="partnership-card-new"
              >
                <PartnerCardHeader>
                  <PartnerCardRadio
                    value={NEW_PARTNER_CARD}
                    inputProps={{
                      "aria-label": "New partner",
                      "data-testid": "partnership-card-new-radio",
                    }}
                  />
                  <PartnerCardIcon isSelected={businessPartnerCard === NEW_PARTNER_CARD}>
                    <AddIcon />
                  </PartnerCardIcon>
                  <PartnerCardTitle>New partner</PartnerCardTitle>
                </PartnerCardHeader>
                <PartnerCardDescription>
                  Create a brand new business partner record.
                </PartnerCardDescription>
              </PartnerCard>
            </PartnerCardGroup>
          </RadioGroup>

          {businessPartnerCard === EXISTING_PARTNER_CARD ? (
          <TwoColumnGrid>
            {renderRestaurantGroupField()}

            <FieldStack>
              <CustomStyledFormFieldText>
                Business partner
                <span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
              </CustomStyledFormFieldText>
              <FormControl fullWidth error={Boolean(errors?.[PARTNER_FIELD])}>
                <StyledSelect
                  value={partnerRestaurantId}
                  displayEmpty
                  disabled={isFormDisabled}
                  onChange={(event) => {
                    setPartnerRestaurantId(event.target.value);
                    clearFieldError(PARTNER_FIELD);
                  }}
                  onClose={handleRestaurantMenuClose}
                  MenuProps={{
                    PaperProps: {
                      ref: restaurantMenuRef,
                      sx: { maxHeight: 400 },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  renderValue={() => restaurantDisplayName || "Select restaurant"}
                  data-testid="partnership-business-partner-select"
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantSearch}
                    placeholder="Search restaurants"
                    isFetching={restaurantsFetching}
                  />
                  {restaurants?.map((option) => (
                    <StyledMenuItem key={option?.id} value={option?.id}>
                      {String(partnerRestaurantId) === String(option?.id) ? (
                        <SelectedMenuItem data={option?.name} />
                      ) : (
                        option?.name
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </StyledSelect>
                <HelperError>{getFirstError(errors?.[PARTNER_FIELD])}</HelperError>
              </FormControl>
            </FieldStack>
          </TwoColumnGrid>
          ) : (
          <>
          <TwoColumnGrid>
            <FieldStack>
              <CustomStyledFormFieldText>
                Partner business name
                <span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
              </CustomStyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                value={newPartnerName}
                disabled={isFormDisabled}
                placeholder="Name"
                onChange={(event) => {
                  setNewPartnerName(event.target.value);
                  clearFieldError("restaurant_name");
                }}
                error={Boolean(errors?.restaurant_name)}
                helperText={getFirstError(errors?.restaurant_name)}
                inputProps={{
                  // restaurants.name is varchar(100); anything longer is a 500, not a 422.
                  "data-testid": "partnership-new-partner-name-input",
                  maxLength: 100,
                }}
              />
            </FieldStack>

            <FieldStack>
              <CustomStyledFormFieldText>
                Phone
                <span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
              </CustomStyledFormFieldText>
              <StyledPhoneInput
                value={`${newPartnerPhoneCountryCode}${newPartnerPhone}`}
                onChange={handlePhoneChange}
                specialLabel=""
                country="us"
                masks={{ us: "(...) ...-....", in: "....-...-...", ca: "(...) ...-...." }}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "8px",
                  borderWidth: "0.25px",
                  gap: "4px",
                  borderColor: errors?.phone_number ? "#d32f2f" : "#D8D9DB",
                }}
                buttonStyle={{ padding: 5, fontSize: 14 }}
                dropdownStyle={{ width: "auto" }}
                inputClass="custom-phone-input"
                searchPlaceholder="Search for country"
                enableSearch
                searchStyle={{
                  height: "35px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  marginRight: "15px",
                }}
                inputProps={{ "data-testid": "partnership-new-partner-phone-input" }}
              />
              <HelperError>{getFirstError(errors?.phone_number)}</HelperError>
            </FieldStack>
          </TwoColumnGrid>

          <TwoColumnGrid>
            <FieldStack>
              <CustomStyledFormFieldText>Email</CustomStyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                value={newPartnerEmail}
                disabled={isFormDisabled}
                placeholder="admin@partner.com"
                onChange={(event) => {
                  setNewPartnerEmail(event.target.value);
                  clearFieldError("email");
                }}
                error={Boolean(errors?.email)}
                helperText={getFirstError(errors?.email)}
                inputProps={{
                  // restaurants.email is varchar(100), same as the name above.
                  "data-testid": "partnership-new-partner-email-input",
                  maxLength: 100,
                }}
              />
            </FieldStack>

            <FieldStack>
              <CustomStyledFormFieldText>Website</CustomStyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                value={newPartnerWebsite}
                disabled={isFormDisabled}
                placeholder="partner.com"
                onChange={(event) => {
                  setNewPartnerWebsite(event.target.value);
                  clearFieldError("website");
                }}
                error={Boolean(errors?.website)}
                helperText={getFirstError(errors?.website)}
                inputProps={{
                  "data-testid": "partnership-new-partner-website-input",
                  maxLength: 255,
                }}
              />
            </FieldStack>
          </TwoColumnGrid>

          <FieldStack>
            <CustomStyledFormFieldText>Address</CustomStyledFormFieldText>
            <StyledTextField
              fullWidth
              size="small"
              value={newPartnerAddress}
              disabled={isFormDisabled}
              placeholder="Street, City, State"
              onChange={(event) => {
                setNewPartnerAddress(event.target.value);
                clearFieldError("address");
              }}
              error={Boolean(errors?.address)}
              helperText={getFirstError(errors?.address)}
              inputProps={{
                "data-testid": "partnership-new-partner-address-input",
                maxLength: 255,
              }}
            />
          </FieldStack>

          <FieldStack>
            <CustomStyledFormFieldText>Partner type</CustomStyledFormFieldText>
            {newPartnerTypeIsCustom ? (
              <StyledTextField
                fullWidth
                size="small"
                value={newPartnerType}
                disabled={isFormDisabled}
                placeholder="Enter partner type"
                autoFocus
                onChange={(event) => {
                  setNewPartnerType(event.target.value);
                  clearFieldError("restaurant_type");
                }}
                error={Boolean(errors?.restaurant_type)}
                helperText={getFirstError(errors?.restaurant_type)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InlineClearIconButton
                        aria-label="Revert to partner type list"
                        onClick={() => {
                          setNewPartnerTypeIsCustom(false);
                          setNewPartnerType("");
                          clearFieldError("restaurant_type");
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </InlineClearIconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  "data-testid": "partnership-new-partner-type-custom-input",
                  maxLength: 100,
                }}
              />
            ) : (
              <FormControl
                fullWidth
                error={Boolean(errors?.restaurant_type)}
                sx={{ position: "relative" }}
              >
                <StyledSelect
                  value={newPartnerType}
                  displayEmpty
                  disabled={isFormDisabled}
                  onChange={handlePartnerTypeChange}
                  renderValue={(selected) => selected || "Select partner type"}
                  sx={{
                    "& .MuiSelect-select": {
                      paddingRight: newPartnerType ? "56px !important" : undefined,
                    },
                  }}
                  data-testid="partnership-new-partner-type-select"
                >
                  {PARTNER_TYPE_OPTIONS.map((option) => (
                    <StyledMenuItem key={option} value={option}>
                      {newPartnerType === option ? <SelectedMenuItem data={option} /> : option}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
                {newPartnerType && (
                  <SelectClearButton
                    aria-label="Clear partner type"
                    onClick={(event) => {
                      event.stopPropagation();
                      setNewPartnerType("");
                      clearFieldError("restaurant_type");
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </SelectClearButton>
                )}
                <HelperError>{getFirstError(errors?.restaurant_type)}</HelperError>
              </FormControl>
            )}
          </FieldStack>

          <PartnerSectionDivider />

          {renderRestaurantGroupField()}
          </>
          )}
          </>
          )}
          </>
          )}

          <FieldStack>
            <CustomStyledFormFieldText>
              Relationship name
              <span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
            </CustomStyledFormFieldText>
            <StyledTextField
              fullWidth
              size="small"
              value={relationshipName}
              disabled={isFormDisabled}
              placeholder="Benefit Program"
              onChange={(event) => {
                setRelationshipName(event.target.value);
                clearFieldError("relationship_name");
              }}
              error={Boolean(errors?.relationship_name)}
              helperText={getFirstError(errors?.relationship_name)}
              inputProps={{
                "data-testid": "partnership-relationship-name-input",
                maxLength: 255,
              }}
            />
          </FieldStack>

          <TwoColumnGrid>
            <FieldStack>
              <CustomStyledFormFieldText>
                Relationship start date
              </CustomStyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                value={relationshipStartDate}
                disabled
                inputProps={{ "data-testid": "partnership-start-date-display" }}
              />
            </FieldStack>

            <FieldStack>
              <CustomStyledFormFieldText>
                Status<span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
              </CustomStyledFormFieldText>
              <FormControl fullWidth error={Boolean(errors?.status)}>
                <StyledSelect
                  value={status}
                  disabled={isFormDisabled}
                  onChange={(event) => {
                    setStatus(event.target.value);
                    clearFieldError("status");
                  }}
                  renderValue={(selectedStatus) =>
                    STATUS_OPTIONS.find((option) => option.value === selectedStatus)?.label || ""
                  }
                  data-testid="partnership-status-select"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <StyledMenuItem key={option.value} value={option.value}>
                      {status === option.value ? (
                        <SelectedMenuItem data={option.label} />
                      ) : (
                        option.label
                      )}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
                <HelperError>{getFirstError(errors?.status)}</HelperError>
              </FormControl>
            </FieldStack>
          </TwoColumnGrid>

          <FieldStack>
            <CustomStyledFormFieldText>Internal notes</CustomStyledFormFieldText>
            <StyledTextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={description}
              disabled={isFormDisabled}
              placeholder="Internal context for this partnership"
              onChange={(event) => {
                setDescription(event.target.value);
                clearFieldError("description");
              }}
              error={Boolean(errors?.description)}
              helperText={getFirstError(errors?.description)}
              inputProps={{
                "data-testid": "partnership-description-input",
                maxLength: 1000,
              }}
            />
          </FieldStack>
        </FormContent>
        )}
      </PartnershipDialogContent>

      <StyledDialogActions>
        <ModalCancelButton
          onClick={onClose}
          disabled={isSubmitting}
          data-testid="partnership-cancel-button"
        >
          Cancel
        </ModalCancelButton>
        <PartnershipSubmitButton
          onClick={handleSubmit}
          variant="contained"
          disabled={isFormDisabled || !isFormComplete}
          data-testid="partnership-submit-button"
        >
          {isSubmitting ? (
            <LoadingSpinner isLoading={true} />
          ) : (
            SUBMIT_LABELS[mode][status]
          )}
        </PartnershipSubmitButton>
      </StyledDialogActions>
    </PartnershipDialog>

    {/* Mounted only once there is something to confirm, so its useNavigate stays out of
        callers that render this modal outside a router. */}
    {createdRestaurant && (
      <PartnershipSuccessModal
        open
        onClose={dismissCreatedRestaurant}
        restaurant={createdRestaurant}
        restaurantGroup={restaurantGroup}
      />
    )}
    </React.Fragment>
  );
};

export const CreatePartnershipModal = (props) => (
  <PartnershipModal {...props} mode={CREATE_MODE} />
);

export const EditPartnershipModal = (props) => (
  <PartnershipModal {...props} mode={EDIT_MODE} />
);

export default CreatePartnershipModal;
